import { ApiError } from "api/client";
import { getNotificationPreferences, updateNotificationPreferences } from "api/notifications";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "Session";

import styles from "./NotificationsPanel.module.css";

/**
 * Notification preferences. Each toggle saves immediately (optimistically, reverting on failure) —
 * there is no separate save button.
 */
const NotificationsPanel = () => {
	const { t } = useTranslation("settingsPage");
	const { getAccessToken, signOut } = useSession();

	const [notifyOnTreeStar, setNotifyOnTreeStar] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");

	useEffect(() => {
		let isCurrent = true;

		const load = async (): Promise<void> => {
			try {
				const token = await getAccessToken();
				if (!token || !isCurrent) {
					return;
				}

				const preferences = await getNotificationPreferences(token);
				if (isCurrent) {
					setNotifyOnTreeStar(preferences.notifyOnTreeStar);
				}
			} catch (error) {
				if (isCurrent && error instanceof ApiError && error.status === 401) {
					signOut();
				}
			} finally {
				if (isCurrent) {
					setIsLoading(false);
				}
			}
		};

		load().catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, [getAccessToken, signOut]);

	const onTreeStarToggle = useCallback(async (): Promise<void> => {
		if (isSaving) {
			return;
		}

		const nextValue = !notifyOnTreeStar;
		setErrorMessage("");
		setNotifyOnTreeStar(nextValue);
		setIsSaving(true);
		try {
			const token = await getAccessToken();
			if (!token) {
				return;
			}

			const saved = await updateNotificationPreferences({ notifyOnTreeStar: nextValue }, token);
			setNotifyOnTreeStar(saved.notifyOnTreeStar);
		} catch (error) {
			setNotifyOnTreeStar(!nextValue);
			setErrorMessage(error instanceof Error ? error.message : t("notifications.saveError"));
		} finally {
			setIsSaving(false);
		}
	}, [getAccessToken, isSaving, notifyOnTreeStar, t]);

	return (
		<div className={styles.panel}>
			<section className={styles.card}>
				<h3 className={styles.cardTitle}>{t("notifications.activity")}</h3>

				{isLoading ? (
					<p className={styles.loading}>{t("notifications.loading")}</p>
				) : (
					<div className={styles.row}>
						<div className={styles.rowText}>
							<span className={styles.rowLabel}>{t("notifications.treeStarLabel")}</span>
							<span className={styles.rowDescription}>{t("notifications.treeStarDescription")}</span>
						</div>
						<label className={styles.switch} htmlFor="settings-notifyOnTreeStar">
							<input
								checked={notifyOnTreeStar}
								className={styles.switchInput}
								disabled={isSaving}
								id="settings-notifyOnTreeStar"
								onChange={onTreeStarToggle}
								type="checkbox"
							/>
							<span aria-hidden="true" className={styles.switchTrack} />
							<span className={styles.switchLabel}>
								{notifyOnTreeStar ? t("notifications.on") : t("notifications.off")}
							</span>
						</label>
					</div>
				)}

				{errorMessage && <p className={styles.noticeError}>{errorMessage}</p>}
			</section>
		</div>
	);
};

export default NotificationsPanel;
